import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Row,
  Col,
  Select,
  Spin,
  Typography,
  Tooltip,
  Space,
  Button,
  Empty,
  Badge,
  Table,
  Tag,
  DatePicker,
  Card,
  Statistic,
  Progress,
} from "antd";
import {
  GiftOutlined,
  ReloadOutlined,
  FireOutlined,
  TrophyOutlined,
  RiseOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  TeamOutlined,
  CalendarOutlined,
  SettingOutlined,
  ExportOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  StarOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  getCreditAnalytics,
  getStreakAnalytics,
  getAchievementAnalytics,
  getCreditLeaderboard,
  exportCreditAnalytics,
} from "@api/credit/credit";
import CreditLeaderboard from "./CreditLeaderboard";
import GrantCreditsModal from "@components/Credits/GrantCreditsModal";

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Metric card component
const MetricCard = ({ title, value, icon, color, suffix, prefix, trend, trendValue }) => {
  const colorMap = {
    blue: { bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", text: "#1d4ed8", gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" },
    green: { bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", text: "#059669", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
    purple: { bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", text: "#7c3aed", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
    orange: { bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", text: "#d97706", gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" },
    pink: { bg: "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)", text: "#db2777", gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)" },
    red: { bg: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)", text: "#dc2626", gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
    teal: { bg: "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)", text: "#0d9488", gradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" },
  };
  const c = colorMap[color] || colorMap.blue;

  const displayValue = () => {
    const formattedValue = typeof value === "number" ? value.toLocaleString() : value;
    return `${prefix || ""}${formattedValue}${suffix || ""}`;
  };

  return (
    <div style={{
      background: c.bg,
      borderRadius: "16px",
      padding: "20px",
      border: "1px solid rgba(0,0,0,0.06)",
      minHeight: "140px",
    }}>
      <div style={{
        width: "44px",
        height: "44px",
        borderRadius: "12px",
        background: c.gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: "18px",
        marginBottom: "14px",
      }}>
        {icon}
      </div>
      <div style={{ fontSize: "28px", fontWeight: 700, color: c.text, lineHeight: 1.1 }}>
        {displayValue()}
      </div>
      <div style={{ fontSize: "13px", fontWeight: 500, color: "#64748b", marginTop: "4px" }}>
        {title}
      </div>
      {trend && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          marginTop: "8px",
          fontSize: "12px",
          color: trend === "up" ? "#10b981" : "#ef4444",
        }}>
          <RiseOutlined style={{ transform: trend === "down" ? "rotate(180deg)" : "none" }} />
          <span>{trendValue}</span>
        </div>
      )}
    </div>
  );
};

// Chart card wrapper
const ChartCard = ({ title, subtitle, badge, children, minHeight = 300, extra }) => (
  <div style={{
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #f1f5f9",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    height: "100%",
  }}>
    <div style={{
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: "1px solid #f1f5f9",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    }}>
      <div>
        <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: 0 }}>
          {title}
        </h3>
        {subtitle && (
          <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>
            {subtitle}
          </p>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {extra}
        {badge !== undefined && (
          <Badge count={badge} style={{ backgroundColor: "#8b5cf6" }} overflowCount={999} />
        )}
      </div>
    </div>
    <div style={{ minHeight }}>{children}</div>
  </div>
);

const CreditAnalytics = () => {
  const history = useHistory();
  const location = useLocation();
  const [grantModalVisible, setGrantModalVisible] = useState(false);

  const getInitialDateRange = () => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get("dateRange");
    const validDateRanges = [7, 30, 90, 365];
    const parsedDate = parseInt(dateParam);
    return validDateRanges.includes(parsedDate) ? parsedDate : 30;
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);

  // Calculate date filters
  const getDateFilters = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - dateRange);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  // Fetch analytics data
  const { data: analytics, isLoading: analyticsLoading, refetch } = useQuery(
    ["creditAnalytics", dateRange],
    async () => {
      const filters = getDateFilters();
      const res = await getCreditAnalytics(filters.startDate, filters.endDate);
      return res.data?.data || res.data;
    },
    { staleTime: 60000, keepPreviousData: true }
  );

  const { data: streakAnalytics } = useQuery(
    ["streakAnalytics"],
    async () => {
      const res = await getStreakAnalytics();
      return res.data?.data || res.data;
    },
    { staleTime: 60000 }
  );

  const { data: achievementAnalytics } = useQuery(
    ["achievementAnalytics"],
    async () => {
      const res = await getAchievementAnalytics();
      return res.data?.data || res.data;
    },
    { staleTime: 60000 }
  );

  const loading = analyticsLoading;

  const updateURL = (newDateRange) => {
    const params = new URLSearchParams(location.search);
    params.set("dateRange", newDateRange.toString());
    history.replace(`${location.pathname}?${params.toString()}`);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    updateURL(newDateRange);
  };

  const handleExport = async () => {
    try {
      const filters = getDateFilters();
      const res = await exportCreditAnalytics("csv", filters.startDate, filters.endDate);

      // Create download link
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `credit-analytics-${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // Process data for display
  const totals = {
    totalCreditsAwarded: analytics?.totalCreditsAwarded || 0,
    activeStreaks: analytics?.activeStreaks || streakAnalytics?.totalActiveStreaks || 0,
    averageStreakLength: analytics?.averageStreakLength || streakAnalytics?.averageStreakLength || 0,
    engagementRate: analytics?.engagementRate || 0,
    creditsBySource: analytics?.creditsBySource || {},
    streakDistribution: analytics?.streakDistribution || streakAnalytics?.streakDistribution || [],
    topAchievements: analytics?.topAchievements || achievementAnalytics?.mostPopular || [],
    creditsOverTime: analytics?.creditsOverTime || [],
  };

  const colors = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4"];

  // Source data for pie chart visualization
  const sourceData = [
    { label: "Daily Login", value: totals.creditsBySource.daily_login || 0, color: "#3b82f6" },
    { label: "Streak Bonus", value: totals.creditsBySource.streak || 0, color: "#10b981" },
    { label: "Gamification", value: totals.creditsBySource.gamification || 0, color: "#f59e0b" },
    { label: "Achievements", value: totals.creditsBySource.achievement || 0, color: "#8b5cf6" },
    { label: "Admin Grant", value: totals.creditsBySource.admin_grant || 0, color: "#ec4899" },
  ].filter(s => s.value > 0);

  const totalSourceCredits = sourceData.reduce((sum, s) => sum + s.value, 0);

  // Streak distribution data
  const streakDistributionData = totals.streakDistribution.length > 0
    ? totals.streakDistribution
    : [
        { range: "1-2 days", count: 0 },
        { range: "3-6 days", count: 0 },
        { range: "7-13 days", count: 0 },
        { range: "14-29 days", count: 0 },
        { range: "30+ days", count: 0 },
      ];

  const maxStreakCount = Math.max(...streakDistributionData.map(s => s.count), 1);

  if (loading && !analytics) {
    return (
      <div style={{
        background: "#f8fafc",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "16px"
      }}>
        <Spin size="large" />
        <Text style={{ color: "#64748b" }}>Loading credit analytics...</Text>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "calc(100vh - 80px)", padding: "24px" }}>
      {/* Header */}
      <div style={{ maxWidth: "1400px", margin: "0 auto 24px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "16px", marginBottom: "8px" }}>
          <div>
            <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1e293b", margin: "0 0 4px 0" }}>
              Credit Rewards Analytics
            </h1>
            <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
              Track credit distribution, streaks, achievements, and user engagement
            </p>
          </div>
          <Space size="small">
            <Select value={dateRange} onChange={handleDateRangeChange} style={{ width: 140 }}>
              <Option value={7}>Last 7 days</Option>
              <Option value={30}>Last 30 days</Option>
              <Option value={90}>Last 90 days</Option>
              <Option value={365}>Last year</Option>
            </Select>
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={() => refetch()}
            />
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              type="primary"
              icon={<GiftOutlined />}
              onClick={() => setGrantModalVisible(true)}
            >
              Grant Credits
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => history.push("/super-admin/credits/config")}
            >
              Configure
            </Button>
          </Space>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={12} sm={6}>
            <MetricCard
              title="Total Credits Awarded"
              value={totals.totalCreditsAwarded}
              icon={<GiftOutlined />}
              color="green"
            />
          </Col>
          <Col xs={12} sm={6}>
            <MetricCard
              title="Active Streaks"
              value={totals.activeStreaks}
              icon={<FireOutlined />}
              color="orange"
            />
          </Col>
          <Col xs={12} sm={6}>
            <MetricCard
              title="Average Streak Length"
              value={totals.averageStreakLength.toFixed(1)}
              icon={<CalendarOutlined />}
              color="purple"
              suffix=" days"
            />
          </Col>
          <Col xs={12} sm={6}>
            <MetricCard
              title="Engagement Rate"
              value={totals.engagementRate.toFixed(1)}
              icon={<RiseOutlined />}
              color="blue"
              suffix="%"
            />
          </Col>
        </Row>

        {/* Charts Row 1 */}
        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          {/* Credits by Source */}
          <Col xs={24} lg={8}>
            <ChartCard title="Credits by Source" subtitle="Distribution of awarded credits" minHeight={320}>
              {sourceData.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {sourceData.map((source, index) => {
                    const percentage = totalSourceCredits > 0
                      ? ((source.value / totalSourceCredits) * 100).toFixed(1)
                      : 0;
                    return (
                      <div key={source.label} style={{
                        padding: "14px 16px",
                        background: index === 0 ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" : "#f8fafc",
                        border: index === 0 ? "1px solid #a7f3d0" : "1px solid #e2e8f0",
                        borderRadius: "10px",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{
                              width: "12px",
                              height: "12px",
                              borderRadius: "4px",
                              background: source.color,
                            }} />
                            <Text strong style={{ fontSize: "13px" }}>{source.label}</Text>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <Text style={{ fontWeight: 700, color: source.color }}>{source.value.toLocaleString()}</Text>
                            <Text type="secondary" style={{ marginLeft: "8px", fontSize: "12px" }}>{percentage}%</Text>
                          </div>
                        </div>
                        <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                          <div style={{
                            width: `${percentage}%`,
                            height: "100%",
                            background: source.color,
                            borderRadius: "3px",
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No credit data" />
              )}
            </ChartCard>
          </Col>

          {/* Streak Distribution */}
          <Col xs={24} lg={8}>
            <ChartCard title="Streak Distribution" subtitle="Users by streak length" minHeight={320}>
              {streakDistributionData.some(s => s.count > 0) ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {streakDistributionData.map((streak, index) => {
                    const widthPercent = (streak.count / maxStreakCount) * 100;
                    const color = colors[index % colors.length];
                    const isBest = streak.count === Math.max(...streakDistributionData.map(s => s.count));
                    return (
                      <div key={streak.range} style={{
                        padding: "12px 14px",
                        background: isBest ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                        border: isBest ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                        borderRadius: "10px",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                          <Text strong style={{ fontSize: "13px" }}>
                            {isBest && <TrophyOutlined style={{ color: "#f59e0b", marginRight: 4 }} />}
                            {streak.range}
                          </Text>
                          <Text style={{ fontWeight: 700, color }}>{streak.count.toLocaleString()}</Text>
                        </div>
                        <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                          <div style={{
                            width: `${Math.max(widthPercent, 3)}%`,
                            height: "100%",
                            background: color,
                            borderRadius: "3px",
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No streak data" />
              )}
            </ChartCard>
          </Col>

          {/* Top Achievements */}
          <Col xs={24} lg={8}>
            <ChartCard
              title="Top Achievements"
              subtitle="Most unlocked achievements"
              badge={totals.topAchievements?.length}
              minHeight={320}
            >
              {totals.topAchievements?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {totals.topAchievements.slice(0, 6).map((achievement, index) => {
                    const maxCount = Math.max(...totals.topAchievements.map(a => a.count), 1);
                    const widthPercent = (achievement.count / maxCount) * 100;
                    const color = colors[index % colors.length];
                    return (
                      <div key={achievement.id || achievement.name} style={{
                        padding: "12px 14px",
                        background: index < 3 ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" : "#f8fafc",
                        border: index < 3 ? "1px solid #fcd34d" : "1px solid #e2e8f0",
                        borderRadius: "10px",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {index < 3 && <CrownOutlined style={{ color: "#f59e0b" }} />}
                            <Text strong style={{ fontSize: "13px" }}>{achievement.name}</Text>
                          </div>
                          <Text style={{ fontWeight: 700, color }}>{achievement.count.toLocaleString()}</Text>
                        </div>
                        <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                          <div style={{
                            width: `${Math.max(widthPercent, 3)}%`,
                            height: "100%",
                            background: color,
                            borderRadius: "3px",
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No achievement data" />
              )}
            </ChartCard>
          </Col>
        </Row>

        {/* Credits Over Time */}
        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          <Col xs={24}>
            <ChartCard
              title="Credits Awarded Over Time"
              subtitle={`Last ${dateRange} days`}
              minHeight={300}
            >
              {totals.creditsOverTime?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {totals.creditsOverTime.slice(-14).map((day, index) => {
                    const maxCredits = Math.max(...totals.creditsOverTime.map(d => d.credits || 0), 1);
                    const widthPercent = ((day.credits || 0) / maxCredits) * 100;
                    const date = new Date(day.date);
                    const isBest = day.credits === Math.max(...totals.creditsOverTime.slice(-14).map(d => d.credits || 0));
                    const color = isBest ? "#10b981" : "#3b82f6";
                    return (
                      <div key={day.date} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ width: "80px", fontSize: "12px", fontWeight: isBest ? 700 : 500, color }}>
                          {date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </div>
                        <div style={{ flex: 1, height: "24px", background: "#f1f5f9", borderRadius: "6px", overflow: "hidden", position: "relative" }}>
                          <div style={{
                            width: `${Math.max(widthPercent, 2)}%`,
                            height: "100%",
                            background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`,
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            paddingRight: widthPercent > 20 ? "8px" : 0,
                          }}>
                            {widthPercent > 20 && (
                              <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>
                                {(day.credits || 0).toLocaleString()}
                              </span>
                            )}
                          </div>
                          {widthPercent <= 20 && (
                            <span style={{
                              position: "absolute",
                              left: `calc(${Math.max(widthPercent, 2)}% + 6px)`,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#64748b",
                            }}>
                              {(day.credits || 0).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div style={{
                    marginTop: "16px",
                    padding: "12px 14px",
                    background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                    borderRadius: "10px",
                    border: "1px solid #a7f3d0",
                    display: "flex",
                    justifyContent: "space-between",
                  }}>
                    <div>
                      <div style={{ fontSize: "11px", color: "#059669", fontWeight: 500 }}>Total (Last {dateRange} days)</div>
                      <div style={{ fontSize: "18px", fontWeight: 700, color: "#047857" }}>
                        {totals.creditsOverTime.reduce((sum, d) => sum + (d.credits || 0), 0).toLocaleString()} credits
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "11px", color: "#059669", fontWeight: 500 }}>Daily Average</div>
                      <div style={{ fontSize: "18px", fontWeight: 700, color: "#047857" }}>
                        {Math.round(totals.creditsOverTime.reduce((sum, d) => sum + (d.credits || 0), 0) / Math.max(totals.creditsOverTime.length, 1)).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No time series data" />
              )}
            </ChartCard>
          </Col>
        </Row>

        {/* Leaderboard */}
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <CreditLeaderboard />
          </Col>
        </Row>
      </div>

      {/* Grant Credits Modal */}
      <GrantCreditsModal
        visible={grantModalVisible}
        onClose={() => setGrantModalVisible(false)}
        onSuccess={() => {
          setGrantModalVisible(false);
          refetch();
        }}
      />
    </div>
  );
};

export default CreditAnalytics;
